import { useState, useCallback } from 'react';

const normalizeChartsResponse = (payload) => {
  if (!payload) return [];

  if (Array.isArray(payload.chartsArray)) {
    return payload.chartsArray.filter(Boolean);
  }

  if (payload.charts && typeof payload.charts === 'object') {
    return Object.values(payload.charts)
      .filter(Boolean)
      .map((chart) => ({
        id: chart.id,
        chartData: chart.chartData,
        gridPosition: chart.gridPosition ?? null,
      }));
  }

  return [];
};

export default function useWizardCharts(options = {}) {
  const { startInLoadingState = false } = options;
  const [charts, setCharts] = useState([]);
  const [loadingCharts, setLoadingCharts] = useState(startInLoadingState);

  const loadCharts = useCallback(async () => {
    setLoadingCharts(true);
    try {
      const response = await fetch('/api/wizard/charts', {
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('Failed to load charts:', response.statusText);
        return [];
      }

      const data = await response.json();
      if (data?.success) {
        const normalized = normalizeChartsResponse(data);
        setCharts(normalized);
        return normalized;
      }
    } catch (error) {
      console.error('Error loading charts:', error);
    } finally {
      setLoadingCharts(false);
    }

    return [];
  }, []);

  const handleChartGenerated = useCallback(async (chartData) => {
    if (!chartData) return false;

    const tempId = `temp-${Date.now()}`;
    const optimisticChart = {
      id: tempId,
      chartData,
      gridPosition: null,
    };

    setCharts((prev) => [...prev, optimisticChart]);

    try {
      const response = await fetch('/api/wizard/save-chart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          chartData,
          gridPosition: null,
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || 'Failed to save chart');
      }

      const data = await response.json();
      if (data?.success && data.chart) {
        setCharts((prev) =>
          prev.map((chart) =>
            chart.id === tempId
              ? {
                  id: data.chart.id,
                  chartData: data.chart.chartData,
                  gridPosition: data.chart.gridPosition ?? null,
                }
              : chart
          )
        );
        return true;
      }

      throw new Error('Failed to save chart');
    } catch (error) {
      console.error('Error saving chart:', error);
      setCharts((prev) => prev.filter((chart) => chart.id !== tempId));
      alert(error.message || 'Failed to save chart. Please try again.');
      return false;
    }
  }, []);

  const handleChartDelete = useCallback(async (chartId) => {
    if (!chartId) return false;

    try {
      const response = await fetch(`/api/wizard/chart/${chartId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete chart');
      }

      setCharts((prev) => prev.filter((chart) => chart.id !== chartId));
      return true;
    } catch (error) {
      console.error('Error deleting chart:', error);
      alert('Failed to delete chart. Please try again.');
      return false;
    }
  }, []);

  const handleChartUpdate = useCallback(async (chartId, updatedChartData) => {
    if (!chartId) {
      console.warn('No chartId provided for update');
      return false;
    }

    try {
      const response = await fetch(`/api/wizard/chart/${chartId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ chartData: updatedChartData }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || 'Failed to update chart');
      }

      const data = await response.json();
      if (!data?.success || !data.chart) {
        throw new Error('Chart update response unsuccessful');
      }

      setCharts((prev) =>
        prev.map((chart) =>
          chart.id === chartId
            ? { ...chart, chartData: data.chart.chartData }
            : chart
        )
      );

      return true;
    } catch (error) {
      console.error('Error updating chart:', error);
      alert(error.message || 'Failed to update chart. Please try again.');
      return false;
    }
  }, []);

  return {
    charts,
    loadingCharts,
    loadCharts,
    handleChartGenerated,
    handleChartDelete,
    handleChartUpdate,
  };
}

